<script setup lang="ts">
import { onMounted, onScopeDispose, ref, watchEffect } from 'vue'
import type { QueryClient } from '@tanstack/vue-query'
import { onlineManager, useQueryClient } from '@tanstack/vue-query'
import type {
    DevtoolsButtonPosition,
    DevtoolsErrorType,
    DevtoolsPosition,
} from '@tanstack/query-devtools'
import { TanstackQueryDevtools } from '@tanstack/query-devtools'

export interface DevtoolsOptions {
    /**
     * Set this true if you want the dev tools to default to being open
     */
    initialIsOpen?: boolean
    /**
     * The position of the React Query logo to open and close the devtools panel.
     * 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
     * Defaults to 'bottom-right'.
     */
    buttonPosition?: DevtoolsButtonPosition
    /**
     * The position of the React Query devtools panel.
     * 'top' | 'bottom' | 'left' | 'right'
     * Defaults to 'bottom'.
     */
    position?: DevtoolsPosition
    /**
     * Custom instance of QueryClient
     */
    client?: QueryClient
    /**
     * Use this so you can define custom errors that can be shown in the devtools.
     */
    errorTypes?: Array<DevtoolsErrorType>
    /**
     * Use this to pass a nonce to the style tag that is added to the document head. This is useful if you are using a Content Security Policy (CSP) nonce to allow inline styles.
     */
    styleNonce?: string
    /**
     * Use this so you can attach the devtool's styles to specific element in the DOM.
     */
    shadowDOMTarget?: ShadowRoot
}

const props = defineProps<DevtoolsOptions>()

const div = ref<HTMLElement>()
const client = props.client || useQueryClient()
const devtools = new TanstackQueryDevtools({
    client,
    queryFlavor: 'Vue Query',
    version: '5',
    onlineManager,
    buttonPosition: props.buttonPosition,
    position: props.position,
    initialIsOpen: props.initialIsOpen,
    errorTypes: props.errorTypes,
    styleNonce: props.styleNonce,
    shadowDOMTarget: props.shadowDOMTarget,
})

watchEffect(() => {
    devtools.setButtonPosition(props.buttonPosition || 'bottom-right')
    devtools.setPosition(props.position || 'bottom')
    devtools.setInitialIsOpen(props.initialIsOpen)
    devtools.setErrorTypes(props.errorTypes || [])
})

onMounted(() => {
    devtools.mount(div.value as HTMLElement)
})

onScopeDispose(() => {
    devtools.unmount()
})
</script>

<template>
    <div ref="div" className="tsqd-parent-container"></div>
</template>
